// NHS Hypertension Case-Finding Service
// 3-stage clinical pathway (Initial → ABPM Fitting → ABPM Follow Up).
// Each stage has its own consultation template and progression is gated:
// a stage can only be started once the previous stage has been completed.
//
// Stage progress is persisted per-patient in sessionStorage so that the
// pharmacist can return to the workflow at any time during the session.

export type HypertensionStageId =
  | "nhs-htn-initial"
  | "nhs-htn-abpm-fitting"
  | "nhs-htn-abpm-followup";

export interface HypertensionStageDefinition {
  id: HypertensionStageId;
  number: 1 | 2 | 3;
  shortLabel: string;
  label: string;
  description: string;
  clinicalLinkRefs?: string[];
}

export const HYPERTENSION_SERVICE_NAME =
  "NHS Hypertension Case-Finding Service";

export const HYPERTENSION_STAGES: HypertensionStageDefinition[] = [
  {
    id: "nhs-htn-initial",
    number: 1,
    shortLabel: "Initial",
    label: "Initial Consultation",
    description:
      "Eligibility, clinic blood pressure measurement and decision to offer ABPM.",
    clinicalLinkRefs: [
      "nice-ng136-bp",
      "nhs-england-htn-case-finding",
      "nhs-htn-info",
    ],
  },
  {
    id: "nhs-htn-abpm-fitting",
    number: 2,
    shortLabel: "ABPM Fitting",
    label: "ABPM Fitting",
    description:
      "Fit ambulatory blood pressure monitor, brief patient and confirm wear period.",
    clinicalLinkRefs: ["nice-ng136-bp", "nhs-abpm-guide"],
  },
  {
    id: "nhs-htn-abpm-followup",
    number: 3,
    shortLabel: "ABPM Follow Up",
    label: "ABPM Follow Up",
    description:
      "Review ABPM readings, confirm diagnosis and refer / signpost as required.",
    clinicalLinkRefs: ["nice-ng136-bp", "nhs-abpm-guide"],
  },
];

export const getStage = (
  id: HypertensionStageId,
): HypertensionStageDefinition => HYPERTENSION_STAGES.find((s) => s.id === id)!;

// ─── Per-patient stage completion tracking ──────────────────────────────────

const STORAGE_KEY = "tv_nhs_htn_progress_v1";

export interface PatientHypertensionProgress {
  // ISO date strings keyed by stage id when completed; missing = not completed
  completed: Partial<Record<HypertensionStageId, string>>;
  // Optional treatment plan id linking the workflow together
  treatmentPlanId?: string;
}

type ProgressMap = Record<string, PatientHypertensionProgress>;

const safeParse = (raw: string | null): ProgressMap => {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
};

// Seed data – ensures certain demo patients arrive in a known stage state.
// e.g. Jessica Aubree (id "1") has Stage 1 already complete and is ready
// for Stage 2 (ABPM Fitting).
const SEED_PROGRESS: ProgressMap = {
  "1": {
    completed: {
      "nhs-htn-initial": "2025-02-10T09:30:00.000Z",
    },
    treatmentPlanId: "tp-jess-htn-case-finding",
  },
  "22": {
    completed: {
      "nhs-htn-initial": "2025-02-10T09:30:00.000Z",
    },
    treatmentPlanId: "tp-jessica-htn-case-finding",
  },
  "18": {
    completed: {
      "nhs-htn-initial": "2025-02-10T10:00:00.000Z",
      "nhs-htn-abpm-fitting": "2025-02-14T11:30:00.000Z",
    },
    treatmentPlanId: "tp-ben-htn-case-finding",
  },
};

const SEED_FLAG_KEY = "tv_nhs_htn_progress_seeded_v1";

const ensureSeeded = (current: ProgressMap): ProgressMap => {
  if (typeof window === "undefined") return current;
  if (window.sessionStorage.getItem(SEED_FLAG_KEY)) return current;
  const merged: ProgressMap = { ...current };
  for (const [k, v] of Object.entries(SEED_PROGRESS)) {
    if (!merged[k]) merged[k] = v;
  }
  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
  window.sessionStorage.setItem(SEED_FLAG_KEY, "1");
  return merged;
};

const readAll = (): ProgressMap => {
  if (typeof window === "undefined") return {};
  const raw = safeParse(window.sessionStorage.getItem(STORAGE_KEY));
  return ensureSeeded(raw);
};

const writeAll = (map: ProgressMap) => {
  if (typeof window === "undefined") return;
  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(map));
};

export const getPatientHypertensionProgress = (
  patientId: string | number,
): PatientHypertensionProgress => {
  const key = String(patientId);
  const all = readAll();
  return all[key] || { completed: {} };
};

export const markHypertensionStageComplete = (
  patientId: string | number,
  stageId: HypertensionStageId,
): PatientHypertensionProgress => {
  const key = String(patientId);
  const all = readAll();
  const current = all[key] || { completed: {} };
  const next: PatientHypertensionProgress = {
    ...current,
    completed: { ...current.completed, [stageId]: new Date().toISOString() },
  };
  all[key] = next;
  writeAll(all);
  return next;
};

export const setPatientHypertensionTreatmentPlanId = (
  patientId: string | number,
  treatmentPlanId: string,
) => {
  const key = String(patientId);
  const all = readAll();
  const current = all[key] || { completed: {} };
  all[key] = { ...current, treatmentPlanId };
  writeAll(all);
};

export const isHypertensionStageUnlocked = (
  patientId: string | number,
  stageId: HypertensionStageId,
): boolean => {
  if (stageId === "nhs-htn-initial") return true;
  const progress = getPatientHypertensionProgress(patientId);
  if (stageId === "nhs-htn-abpm-fitting") {
    return Boolean(progress.completed["nhs-htn-initial"]);
  }
  if (stageId === "nhs-htn-abpm-followup") {
    return Boolean(progress.completed["nhs-htn-abpm-fitting"]);
  }
  return false;
};

export const getPreviousStage = (
  stageId: HypertensionStageId,
): HypertensionStageDefinition | null => {
  const idx = HYPERTENSION_STAGES.findIndex((s) => s.id === stageId);
  if (idx <= 0) return null;
  return HYPERTENSION_STAGES[idx - 1];
};
